export class ConfigProvider {
    private configList: any[];
    private configMap: Map<string, any> = new Map();

    constructor(configList: any[]) {
        this.configList = configList;

        for (let config of this.configList) {
            this.configMap.set(config.id, config);
        }
    }

    public useWhDataForReversalsConfig(): boolean {
        const useWhDataForReversalsConfig = this.configMap.get(
            "properties/use_warehouse_data_for_reversal"
        );
        return (
            (useWhDataForReversalsConfig &&
                useWhDataForReversalsConfig.value) ||
            false
        );
    }

    public calendarConfig(): any {
        const d = this.configMap.get("properties/calendar");
        return (
            (d && d.value && d.value.config) || {
                type: "default"
            }
        );
    }

    public unearnedByValueConfig(defaultValue: string = "customer"): string {
        const unearnedByConfig = this.configMap.get("properties/unearned_by");
        return (unearnedByConfig && unearnedByConfig.value) || defaultValue;
    }

    public recognizeByTransactionDateConfig(): boolean {
        const recognizeByTransactionDateConfig = this.configMap.get(
            "properties/recognize-by-transaction-date"
        );
        return (
            recognizeByTransactionDateConfig &&
            recognizeByTransactionDateConfig.value
        );
    }

    public psdByProductCodeConfig(): boolean {
        const psdByProductCodeConfig = this.configMap.get(
            "properties/service-delivery-by-product-code"
        );
        return psdByProductCodeConfig && psdByProductCodeConfig.value;
    }

    public endDateInclusiveConfig(): boolean {
        const endDateInclusiveConfig = this.configMap.get(
            "properties/ratable_end_date_inclusive"
        );
        return endDateInclusiveConfig && endDateInclusiveConfig.value;
    }

    public disableCreditNoteAllocationConfig(): boolean {
        const disableCnAllocationConfig = this.configMap.get(
            "properties/contract-term/disable-cn-allocation"
        );
        return disableCnAllocationConfig && disableCnAllocationConfig.value;
    }

    public trackPendingCollectibilityLevel(): string {
        const c = this.configMap.get(
            "properties/track-pending-collectability/level"
        );
        return c && c.value;
    }

    public multiCurrencyEnabled(): boolean {
        const c = this.configMap.get("properties/multi-currency-enabled");
        return (c && c.value) || false;
    }

    public autoCreateSSP(): boolean {
        const autoCreateSSPConfig = this.configMap.get(
            "properties/auto-create-ssp"
        );
        return (autoCreateSSPConfig && autoCreateSSPConfig.value) || false;
    }

    public sspFieldConfig(): any[] {
        let sspFieldsConfig = this.configMap.get("properties/ssp-fields");
        return (sspFieldsConfig && sspFieldsConfig.value) || [];
    }

    public isMeteredEnabled(): boolean {
        let meteredCfg = this.configMap.get(
            "properties/metered_connection_enabled"
        );
        return (meteredCfg && meteredCfg.value) || false;
    }

    public customFieldsConfig(): any {
        const cfConfig = this.configMap.get("customFields/attributeMapping");
        return (cfConfig && cfConfig.value) || {};
    }

    public customFieldsValues(): any {
        const cfConfig = this.customFieldsConfig();
        return cfConfig && Object.values(cfConfig);
    }

    public maxContractYearsConfig(): number {
        const d3 = this.configMap.get("properties/max-contract-years");
        return (d3 && d3.value && Number(d3.value)) || 15;
    }

    public dereferenceRuleHasPriority(): boolean {
        const d = this.configMap.get(
            "properties/dereference_rule_has_priority"
        );
        return (d && d.value) || false;
    }

    public dereferenceRuleHasPrioryDisableProduct(): boolean {
        const d = this.configMap.get(
            "properties/dereference_rule_has_priority/disable-product"
        );
        return (d && d.value) || false;
    }

    public useProduceListPriceConfig(): boolean {
        const useProductListPriceConfig = this.configMap.get(
            "properties/enable_list_price_calc"
        );
        return (
            (useProductListPriceConfig && useProductListPriceConfig.value) ||
            false
        );
    }

    public expenseReasonCode(): any[] {
        const reasonCodeConfig = this.configMap.get(
            "properties/expense/reasoncodes"
        );
        return (reasonCodeConfig && reasonCodeConfig.value) || [];
    }

    public cnAllocationByBacklog(): any[] {
        const config = this.configMap.get(
            "properties/revenue/credit-note/by-backlog"
        );
        return (config && config.value) || false;
    }

    public deferredByItem(): boolean {
        const deferredByItemConfig = this.configMap.get(
            "properties/deferred_by_item"
        );
        return (deferredByItemConfig && deferredByItemConfig.value) || false;
    }

    public useSubscriptionInfo(): boolean {
        const c = this.configMap.get("integration/use-subscription-info");
        return (c && c.value) || false;
    }

    public referenceInvoiceEnabled(): boolean {
        const c = this.configMap.get(
            "properties/billing/reference-invoice-enabled"
        );
        return (c && c.value) || false;
    }

    public billingByContract(): boolean {
        const c = this.configMap.get("properties/billing/by-contract");
        return (c && c.value) || false;
    }

    public billingByProductCode(): boolean {
        const c = this.configMap.get("properties/billing/by-product-code");
        return (c && c.value) || false;
    }

    public shortLongTermRevenueEnabled(): boolean {
        const c = this.configMap.get(
            "properties/enabled_short_long_term_revenue"
        );
        return (c && c.value) || false;
    }

    public updateClosedPeriodAggregationFields(): UpdateClosedPeriodAggregationFields {
        const updateAggregationJoinField = this.configMap.get(
            "properties/update_closed_period_agg_fields"
        );
        return (
            (updateAggregationJoinField && updateAggregationJoinField.value) ||
            {}
        );
    }

    public revenueRoundPrecision(): number {
        const c = this.configMap.get("properties/revenue/round-precision");
        return c && c.value && Number(c.value);
    }

    public createSinglePoItem(): boolean {
        const c = this.configMap.get("properties/create-single-po-item");
        return (c && c.value) || false;
    }

    public deferredRevenueAllocation(): boolean {
        const c = this.configMap.get(
            "properties/deferred_revenue_by_revneue_allocation"
        );
        return (c && c.value) || false;
    }

    public setSalePriceForBundleComponents(): boolean {
        const c = this.configMap.get(
            "properties/set_sale_price_for_bundle_components"
        );
        return (c && c.value) || false;
    }

    public loadDataMaxConnection(): boolean {
        const c = this.configMap.get("properties/load-data-max-conn");
        return c && c.value;
    }

    public loadDataMaxSql(): boolean {
        const c = this.configMap.get("properties/load-data-max-sql");
        return c && c.value;
    }

    public mstrAnalyticsEnabled(): boolean {
        const c = this.configMap.get("properties/mstr_analytics_enabled");
        return c && c.value;
    }

    public itemBillingMetric(): boolean {
        const c = this.configMap.get(
            "properties/contract-term/item-billing-metric/enabled"
        );
        return (c && c.value) || false;
    }

    public deferredUnbilledByItem(): boolean {
        const c = this.configMap.get("properties/deferred-unbilled/by-item");
        return (c && c.value) || false;
    }

    public revenueEngineModel(): boolean {
        const c = this.configMap.get("properties/revenue-engine-model/enabled");
        return (c && c.value) || false;
    }

    public enableProductBlending(): boolean {
        const c = this.configMap.get("properties/ssp/use-product-blended-rate");
        return (c && c.value) || false;
    }

    public preservedAdjustmentTypes(): string[] {
        const c = this.configMap.get("properties/preserved-adjustment-types");
        return (c && c.value) || [];
    }

    public preservedBillingCreationTypes(): string[] {
        const c = this.configMap.get(
            "properties/preserved-billing-creation-types"
        );
        return (c && c.value) || [];
    }
}

interface UpdateClosedPeriodAggregationFields {
    referenceInvoiceNumber?: boolean;
    invoiceNumber?: boolean;
    subscriptionId?: boolean;
    customerId?: boolean;
}
